/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService, type QueryParams } from './base'

const baseURL = '/api/autofilesetting'

export interface AutoFileSetting {
  id?: number
  name?: string
  enabled?: boolean
  folderTypeId?: number
  studyFolderId?: number
  deleted?: boolean
  [key: string]: unknown
}

class AutoFileApiService {
  async getAll(params?: QueryParams) {
    return baseApiService.fetch(baseURL, {
      params
    })
  }

  async update(data: AutoFileSetting[], originalRcrAutofileData: AutoFileSetting[]): Promise<void> {
    const copyOfItems = JSON.parse(JSON.stringify(data))

    const addedItems = copyOfItems.filter((item: AutoFileSetting) => !item.id)
    const deletedItems = copyOfItems.filter((item: AutoFileSetting) => !!item.deleted)
    const updatedItems = copyOfItems.filter((item: AutoFileSetting) => {
      const originalItem = originalRcrAutofileData.find((original) => original.id === item.id)
      if (originalItem && !item.deleted) {
        return JSON.stringify(item) !== JSON.stringify(originalItem)
      }
      return false
    })

    const promises: Promise<unknown>[] = []

    if (addedItems.length) {
      promises.push(this.createBatch(addedItems))
    }

    if (updatedItems.length) {
      promises.push(this.updateBatch(updatedItems))
    }

    if (deletedItems.length) {
      const ids = deletedItems.map((item: AutoFileSetting) => item.id).filter(Boolean) as number[]
      promises.push(this.deleteBatch(ids))
    }

    await Promise.all(promises)
  }

  async createBatch(data: AutoFileSetting[]): Promise<void> {
    return baseApiService.fetch<void>(`${baseURL}/createmultiple`, {
      method: 'POST',
      body: data as unknown as Record<string, unknown>
    })
  }

  async updateBatch(data: AutoFileSetting[]): Promise<void> {
    return baseApiService.fetch<void>(`${baseURL}/updatemultiple`, {
      method: 'PUT',
      body: data as unknown as Record<string, unknown>
    })
  }

  async deleteBatch(ids: number[]): Promise<void> {
    return baseApiService.fetch<void>(baseURL, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8'
      },
      body: { ids }
    })
  }
}

export const autoFileApiService = new AutoFileApiService()
